import clsx from "clsx";

/**
 * Shimmer primitives.
 *
 * A tinted base with a travelling highlight, rather than a pulsing block — the sweep
 * reads as "work in progress" instead of "this element is broken". Both layers use
 * theme tokens, so it inverts correctly in dark mode.
 *
 * These are for a genuine first load only. When data is merely being *refreshed*, the
 * dashboard holds the previous render at reduced opacity instead — swapping populated
 * charts for skeletons causes a layout jump and reads as a failure.
 */
export function Shimmer({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={clsx("relative overflow-hidden bg-skeleton", className)}
      style={style}
    >
      <div className="absolute inset-0 animate-shimmer bg-[linear-gradient(90deg,transparent_0%,var(--color-bg-overlay-hover)_50%,transparent_100%)] bg-[length:200%_100%]" />
    </div>
  );
}

/** A run of text lines with a deliberately ragged last line, like real copy. */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={clsx("flex flex-col gap-2.5", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer
          key={i}
          // Last line short, so the block reads as a paragraph rather than a table.
          className={clsx("h-3 rounded-full", i === lines - 1 ? "w-2/3" : "w-full")}
        />
      ))}
    </div>
  );
}

/** Label + meter + sub-labels — mirrors the real Collections / Payment Plans rows. */
export function SkeletonMeterRow() {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-4">
        <Shimmer className="h-3 w-1/2 rounded-full" />
        <Shimmer className="h-3 w-16 rounded-full" />
      </div>
      <Shimmer className="h-1.5 w-full rounded-full" />
      <div className="flex items-center justify-between gap-4">
        <Shimmer className="h-2.5 w-20 rounded-full" />
        <Shimmer className="h-2.5 w-20 rounded-full" />
      </div>
    </div>
  );
}

export function SkeletonMeters({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-6">
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonMeterRow key={i} />
      ))}
    </div>
  );
}

/**
 * Placeholder bars for a plot area.
 *
 * Heights follow a fixed, uneven pattern rather than random values: a re-render must
 * not reshuffle the skeleton, and a flat row of equal bars looks like a rendered
 * chart with no data.
 */
const BAR_HEIGHTS = [42, 68, 55, 82, 47, 73, 60, 88, 51, 66, 78, 44];

export function SkeletonChart({ className }: { className?: string }) {
  return (
    <div className={clsx("flex items-end gap-2 w-full", className)}>
      {BAR_HEIGHTS.map((h, i) => (
        <div key={i} className="flex-1 flex items-end" style={{ height: "100%" }}>
          <Shimmer
            className="w-full rounded-t-md rounded-b-sm"
            style={{ height: `${h}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function SkeletonStatTile() {
  return (
    <div className="rounded-2xl border border-edge bg-surface p-5 flex items-center gap-4">
      <Shimmer className="w-11 h-11 rounded-xl shrink-0" />
      <div className="flex flex-col gap-2.5 flex-1">
        <Shimmer className="h-2.5 w-20 rounded-full" />
        <Shimmer className="h-5 w-16 rounded-md" />
      </div>
    </div>
  );
}
